import React, { useEffect, useState } from 'react';
import Modal from '../../components/shared/Modal/Modal';
import Button from '../../components/shared/Button/Button';
import StatusBadge from '../../components/shared/StatusBadge/StatusBadge';
import SaleDetails from '../../components/sales/SaleDetails';
import { isVoided, saleStatus } from '../../components/sales/saleLabels';
import { FormActions, FormGroup, FormLabel, FormTextarea, FormHint, ErrorMessage } from '../../components/shared/Form/Form.styles';
import { getSale, voidSale } from '../../services/salesApi';
import { StaffSessionError } from '../../services/staffApi';
import { Notice } from './Sales.styles';

const REASON_MAX = 500;

// Modal de la marca para anular (reemplaza window.confirm). Solo el dueño.
const VoidSaleModal = ({ sale, onClose, onVoided }) => {
  const [reason, setReason] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  const handleVoid = async () => {
    setError('');
    setBusy(true);
    try {
      const data = await voidSale(sale.id, reason.trim());
      onVoided(data.sale, data.message);
    } catch (e) {
      setError(e.message);
      setBusy(false);
    }
  };

  return (
    <Modal title={`¿Anular la venta #${sale.ticketNumber}?`} onClose={busy ? null : onClose} maxWidth="480px" labelledBy="void-title">
      <p style={{ fontSize: 'var(--text-sm)', color: 'var(--text-secondary)', lineHeight: 1.55, marginBottom: 14 }}>
        Se libera el cupo y la venta queda como <strong>anulada</strong>; el ticket conserva su número y el QR deja de servir.
        <strong> Devuelve el dinero al cliente en el lugar</strong>: BokerHub no mueve dinero de las ventas en persona.
        Esta acción no se puede deshacer.
      </p>
      {error && <ErrorMessage role="alert">{error}</ErrorMessage>}
      <FormGroup>
        <FormLabel htmlFor="void-reason">Motivo (opcional)</FormLabel>
        <FormTextarea
          id="void-reason"
          rows={3}
          maxLength={REASON_MAX}
          value={reason}
          disabled={busy}
          onChange={(e) => setReason(e.target.value)}
        />
        <FormHint>{reason.length}/{REASON_MAX}</FormHint>
      </FormGroup>
      <FormActions>
        <Button type="button" variant="ghost" onClick={onClose} disabled={busy}>Conservar venta</Button>
        <Button type="button" variant="danger" onClick={handleVoid} disabled={busy}>
          {busy ? 'Anulando…' : 'Anular venta'}
        </Button>
      </FormActions>
    </Modal>
  );
};

// Detalle de una venta: datos, QR (se vuelve a pedir al backend) y, solo para
// el dueño, "Anular venta".
const SaleDetailModal = ({ mode, sale: initial, canVoid, onClose, onChanged, onSessionError }) => {
  const [sale, setSale] = useState(initial);
  const [qrImage, setQrImage] = useState('');
  const [loadError, setLoadError] = useState('');
  const [voiding, setVoiding] = useState(false);
  const [notice, setNotice] = useState('');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = await getSale(mode, initial.id);
        if (cancelled) return;
        setSale(data.sale);
        setQrImage(data.qrCode || '');
      } catch (error) {
        if (cancelled) return;
        if (error instanceof StaffSessionError) {
          onSessionError(error);
          return;
        }
        setLoadError(error.message);
      }
    })();
    return () => { cancelled = true; };
  }, [mode, initial.id, onSessionError]);

  const state = saleStatus(sale);
  const voided = isVoided(sale);
  const used = sale.status === 'completed';

  const handleVoided = (updated, message) => {
    setSale(updated);
    setQrImage('');
    setVoiding(false);
    setNotice(message || 'Venta anulada. Devuelve el dinero al cliente en el lugar.');
    onChanged(updated);
  };

  return (
    <>
      <Modal title={`Venta #${sale.ticketNumber}`} onClose={onClose} maxWidth="600px" labelledBy="sale-detail-title">
        <div style={{ marginBottom: 12 }}>
          <StatusBadge status={state.status}>{state.label}</StatusBadge>
        </div>
        {notice && <Notice role="status">{notice}</Notice>}
        {loadError && <ErrorMessage role="alert">{loadError}</ErrorMessage>}

        {!voided && qrImage && (
          <div style={{ textAlign: 'center', marginBottom: 14 }}>
            <img
              src={qrImage}
              alt={`Código QR del ticket ${sale.ticketNumber}`}
              style={{ width: '100%', maxWidth: 220, height: 'auto', border: '1px solid var(--border-light)', borderRadius: 'var(--radius-md)', padding: 8, background: 'var(--white)' }}
            />
            <p style={{ fontSize: 'var(--text-xs)', color: 'var(--text-secondary)', marginTop: 6 }}>
              Solo valida la entrada; el pago se hizo en persona.
            </p>
          </div>
        )}

        <SaleDetails sale={sale} />

        {canVoid ? (
          <>
            {used && !voided && (
              <FormHint style={{ marginTop: 12 }}>No se puede anular: el QR ya se usó para entrar.</FormHint>
            )}
            <FormActions>
              <Button type="button" variant="ghost" onClick={onClose}>Cerrar</Button>
              {!voided && (
                <Button type="button" variant="danger" onClick={() => setVoiding(true)} disabled={used}>
                  Anular venta
                </Button>
              )}
            </FormActions>
          </>
        ) : (
          <>
            {!voided && (
              <FormHint style={{ marginTop: 12 }}>
                Si hubo un error en esta venta, pide al dueño de la empresa que la anule.
              </FormHint>
            )}
            <FormActions>
              <Button type="button" onClick={onClose}>Cerrar</Button>
            </FormActions>
          </>
        )}
      </Modal>

      {voiding && (
        <VoidSaleModal sale={sale} onClose={() => setVoiding(false)} onVoided={handleVoided} />
      )}
    </>
  );
};

export default SaleDetailModal;
